import { add, greaterThan, subtract } from "dinero.js";
import React, { type JSX } from "react";

import { ErrorBoundary } from "../../../components/ErrorBoundary";
import { IBasket } from "../../../models/catalogue";
import {
    PriceType,
    getPostDiscountAddonsTotal,
    getProductPrice,
} from "../../../utils/catalogue";
import { Dinero, ZERO, getDinero } from "../../../utils/money";
import { RECYCLING_FEE_NAME } from "../constants";
import { IReduxAPIData, IReduxDerivedData } from "../reducers.interfaces";
import { BasketSavingsRow } from "./BasketSavingsRow";
import { GrantTotalRow } from "./GrantTotalRow";
import { RecyclingFeeRow } from "./RecyclingFeeRow";
import { SalesTaxRow } from "./SalesTaxRow";
import { ShippingCostRow } from "./ShippingCostRow";
import { ShippingDiscountRow } from "./ShippingDiscountRow";
import { SubtitleRow } from "./SubtitleRow";

import styles from "./common.module.scss";

const getRetailSubtotal = (basket: IBasket): Dinero => {
    const subtotal = basket.lines
        .filter((line) => !line.parent)
        .map((line) =>
            getProductPrice(line.product.price, {
                priceType: PriceType.RETAIL,
                includePostDiscountAddons: false,
                quantity: line.quantity,
            }),
        )
        .reduce((total, retail) => add(total, retail), ZERO);
    return subtotal;
};

const getBasketPostDiscountAddonsTotal = (basket: IBasket): Dinero => {
    const subtotal = basket.lines
        .map((line) =>
            getPostDiscountAddonsTotal(line.product.price, line.quantity),
        )
        .reduce((total, addons) => add(total, addons), ZERO);
    return subtotal;
};

const getBasketTaxes = ({
    basket,
    shippingInclTax,
    shippingExclTax,
}: {
    basket: IBasket;
    shippingInclTax: Dinero;
    shippingExclTax: Dinero;
}): {
    totalTax: Dinero | null;
    recyclingFee: Dinero | null;
} => {
    if (!basket.is_tax_known) {
        return {
            totalTax: null,
            recyclingFee: null,
        };
    }
    const shippingTax = subtract(shippingInclTax, shippingExclTax);
    const recyclingFee = getDinero(
        basket.tax_details[RECYCLING_FEE_NAME] || ZERO,
    );
    const totalTax = subtract(
        add(getDinero(basket.total_tax), shippingTax),
        recyclingFee,
    );
    return {
        totalTax: totalTax,
        recyclingFee: greaterThan(recyclingFee, ZERO) ? recyclingFee : null,
    };
};

export const BasketSummary = (props: {
    disableShipping?: boolean;
    disableFreeShippingMessage?: boolean;
    disableTaxes?: boolean;
    derived: IReduxDerivedData;
    data: IReduxAPIData;
    removeVoucherCode: (code: string) => void;
    getExtraContent: () => JSX.Element | null;
}): React.ReactNode => {
    if (!props.data.basket) {
        return null;
    }
    const retailSubtotal = getRetailSubtotal(props.data.basket);
    const addonsSubtotal = getBasketPostDiscountAddonsTotal(props.data.basket);
    const retailSubtotalIncludingAddons = add(retailSubtotal, addonsSubtotal);
    const showTax = props.data.basket.is_tax_known && !props.disableTaxes;
    const { totalTax, recyclingFee } = getBasketTaxes({
        basket: props.data.basket,
        shippingInclTax: getDinero(props.derived.shipping_method_cost_incl_tax),
        shippingExclTax: getDinero(props.derived.shipping_method_cost_excl_tax),
    });
    return (
        <div className="">
            <ul className={styles.pricesContainer}>
                <SubtitleRow
                    retailSubtotalIncludingAddons={
                        retailSubtotalIncludingAddons
                    }
                />
                <BasketSavingsRow
                    basket={props.data.basket}
                    retailSubtotal={retailSubtotal}
                    totalDiscount={
                        props.derived.total_discount
                            ? getDinero(props.derived.total_discount)
                            : ZERO
                    }
                    removeVoucherCode={props.removeVoucherCode}
                />
                <ShippingCostRow
                    disableShipping={props.disableShipping}
                    disableFreeShippingMessage={
                        props.disableFreeShippingMessage
                    }
                    costBeforeDiscountExclTax={
                        props.derived
                            .shipping_method_cost_before_discount_excl_tax
                    }
                />
                <ShippingDiscountRow
                    disableShipping={props.disableShipping}
                    disableFreeShippingMessage={
                        props.disableFreeShippingMessage
                    }
                    costBeforeDiscountExclTax={
                        props.derived
                            .shipping_method_cost_before_discount_excl_tax
                    }
                    costAfterDiscountExclTax={
                        props.derived.shipping_method_cost_excl_tax
                    }
                />
                <SalesTaxRow totalTax={totalTax} />
                {showTax && recyclingFee && (
                    <RecyclingFeeRow recyclingFee={recyclingFee} />
                )}
                <GrantTotalRow
                    basket={props.data.basket}
                    shippingExclTax={getDinero(
                        props.derived.shipping_method_cost_excl_tax || ZERO,
                    )}
                    grandTotal={
                        props.derived.grand_total
                            ? getDinero(props.derived.grand_total)
                            : null
                    }
                    disableShipping={!!props.disableShipping}
                    disableTaxes={!!props.disableTaxes}
                />
                <ErrorBoundary>{props.getExtraContent()}</ErrorBoundary>
            </ul>
        </div>
    );
};
